import { useState, useEffect, useRef } from "react";
import { Phone, X, MapPin } from "lucide-react";
import { C } from "./data";
import { hasSession, createSos, cancelSos } from "./consumerAuth";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export default function Emergency({ onCancel }) {
  const [t, setT] = useState(0);
  const [ref, setRef] = useState(null);
  const [loc, setLoc] = useState(null);
  const incidentId = useRef(null);
  const fired = useRef(false);

  useEffect(() => { const id = setInterval(() => setT((x) => x + 1), 1000); return () => clearInterval(id); }, []);

  useEffect(() => {
    if (!hasSession() || fired.current) return;
    fired.current = true;
    const send = (payload) => createSos(payload).then((r) => { setRef(r.ref); incidentId.current = r.id; }).catch((e) => console.error("SOS create failed", e));
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => { const c = { lat: +p.coords.latitude.toFixed(5), lng: +p.coords.longitude.toFixed(5) }; setLoc(c); send(c); },
        () => send({}), { timeout: 6000 }
      );
    } else send({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async () => {
    if (incidentId.current) { try { await cancelSos(incidentId.current); } catch (e) { console.error("SOS cancel failed", e); } }
    onCancel();
  };

  return (
    <div style={{ position: "relative", minHeight: "100%", background: "#2a0508", color: "#fff", overflow: "hidden" }} data-testid="consumer-emergency">
      <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 50% 34%, ${C.red}55 0%, transparent 62%)`, animation: "sos-pulse 2s ease-in-out infinite" }} />
      <style>{`@keyframes sos-pulse{0%,100%{opacity:.5}50%{opacity:1}}`}</style>
      <div style={{ position: "relative", zIndex: 2, padding: "22px 20px 26px", minHeight: "100%", display: "flex", flexDirection: "column" }}>
        <button onClick={cancel} data-testid="emergency-cancel"
          style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 14px", background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.28)", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>
          <X size={15} /> Cancel panic
        </button>

        <div style={{ textAlign: "center", marginTop: 26 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 9, padding: "9px 18px", background: C.red, borderRadius: 999, fontSize: 13, fontWeight: 700, letterSpacing: "0.1em" }} data-testid="emergency-timer">
            <span style={{ width: 9, height: 9, background: "#fff", borderRadius: "50%", animation: "sos-pulse 1s infinite" }} /> SOS ACTIVE · {fmt(t)}
          </div>
          <div style={{ marginTop: 16, fontFamily: "'Fraunces', Georgia, serif", fontSize: 26, fontWeight: 500 }}>Help is on the way.</div>
          <div style={{ fontSize: 13, opacity: 0.75, marginTop: 6 }}>Stay where you are if it's safe to do so.</div>
        </div>

        <div style={{ marginTop: 22, padding: 15, background: "rgba(0,0,0,0.4)", borderRadius: 12, backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.12)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 10, letterSpacing: "0.1em", opacity: 0.6, textTransform: "uppercase", marginBottom: 6 }}><MapPin size={12} /> Your location</div>
          {loc ? (
            <div style={{ fontSize: 13, opacity: 0.9, fontFamily: "'JetBrains Mono', monospace" }}>{loc.lat}, {loc.lng} · GPS live</div>
          ) : (
            <div style={{ fontSize: 13, opacity: 0.7 }}>Acquiring GPS location…</div>
          )}
        </div>

        <div style={{ marginTop: "auto", textAlign: "center" }}>
          <div style={{ fontSize: 11, opacity: 0.65, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 9 }}>If the app fails, tap to call</div>
          <a href="tel:0664721844" data-testid="emergency-fallback-call"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "22px 20px", background: "#fff", color: C.red, borderRadius: 16, fontSize: 30, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", textDecoration: "none", boxShadow: "0 8px 30px rgba(0,0,0,0.4)" }}>
            <Phone size={26} fill={C.red} /> 066 472 1844
          </a>
          <div style={{ fontSize: 11, opacity: 0.6, marginTop: 8 }}>Control Room · 24/7</div>
        </div>

        <div style={{ marginTop: 18, padding: 12, background: "rgba(0,0,0,0.5)", borderRadius: 8, fontSize: 12, fontFamily: "'JetBrains Mono', monospace", borderLeft: `3px solid ${C.green}` }} data-testid="emergency-feed">
          <div style={{ opacity: 0.85 }} data-testid="emergency-ref">{ref ? `Logged as ${ref} · control room notified` : "Dispatching to control room…"}</div>
          <div style={{ opacity: 0.6, marginTop: 3 }}>{loc ? "Location resolved · sent to control room" : "Resolving location…"}</div>
        </div>
      </div>
    </div>
  );
}
